import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, X, Gift } from "lucide-react";
import Mascot from "@/components/Mascot";
import { useT } from "@/i18n/I18nContext";

const COPY = {
  de: {
    title: "Deine Gratis-Foto-Analysen sind für diesen Monat aufgebraucht",
    body: "Stark, dass du so aktiv bist! Deine 5 Gratis-Foto-Analysen sind für diesen Monat genutzt. Mit Pro analysierst du unbegrenzt weiter — und bekommst Wochenmenüs, Vorratskammer, Restaurant-KI und deinen Coach dazu.",
    lastTitle: "Das war deine letzte Gratis-Foto-Analyse 🎉",
    lastBody: "Hat geklappt! Du hast alle 5 Gratis-Foto-Analysen dieses Monats genutzt — schön, dass du dranbleibst. Mit Pro machst du unbegrenzt weiter und bekommst Wochenmenüs, Vorratskammer, Restaurant-KI und deinen Coach dazu.",
    featureTitle: "Das ist ein Pro-Feature ✨",
    featureBody: "Wochenmenüs, Vorratskammer, Restaurant-KI, Coach & Co. gehören zu Solano Essence Pro. Deine 5 Gratis-Foto-Analysen fürs Essens-Tracking hast du trotzdem jeden Monat.",
    reset: "Deine Gratis-Foto-Analysen füllen sich am Monatsanfang automatisch wieder auf.",
    yearlyBadge: "2 Monate geschenkt",
    yearlyLine: "Jahresabo CHF 99 statt CHF 118.80 — entspricht CHF 8.25/Monat",
    cta: "Pro entdecken",
    later: "Vielleicht später",
  },
  en: {
    title: "Your free photo analyses are used up for this month",
    body: "Love the dedication! You've used your 5 free photo analyses this month. With Pro you keep analysing without limits — plus weekly menus, pantry, restaurant AI and your coach.",
    lastTitle: "That was your last free photo analysis 🎉",
    lastBody: "Done! You've used all 5 free photo analyses this month — love the consistency. With Pro you simply keep going without limits, plus weekly menus, pantry, restaurant AI and your coach.",
    featureTitle: "This is a Pro feature ✨",
    featureBody: "Weekly menus, pantry, restaurant AI, coach & more are part of Solano Essence Pro. You still get your 5 free photo analyses for food tracking every month.",
    reset: "Your free photo analyses refill automatically at the start of each month.",
    yearlyBadge: "2 months free",
    yearlyLine: "Yearly plan: pay 10 months, get 12 — that's just CHF 8.25/month",
    cta: "Discover Pro",
    later: "Maybe later",
  },
  it: {
    title: "Le tue analisi foto gratuite sono finite per questo mese",
    body: "Che grinta! Hai usato le tue 5 analisi foto gratuite di questo mese. Con Pro continui ad analizzare senza limiti — più menu settimanali, dispensa, AI ristorante e il tuo coach.",
    lastTitle: "Era la tua ultima analisi foto gratuita 🎉",
    lastBody: "Fatto! Hai usato tutte le 5 analisi foto gratuite di questo mese — bella costanza. Con Pro continui semplicemente senza limiti, più menu settimanali, dispensa, AI ristorante e il tuo coach.",
    featureTitle: "Questa è una funzione Pro ✨",
    featureBody: "Menu settimanali, dispensa, AI ristorante, coach e altro fanno parte di Solano Essence Pro. Le tue 5 analisi foto gratuite per il diario restano comunque ogni mese.",
    reset: "Le analisi foto gratuite si ricaricano automaticamente all'inizio di ogni mese.",
    yearlyBadge: "2 mesi in regalo",
    yearlyLine: "Piano annuale: paghi 10 mesi, ne ricevi 12 — solo CHF 8.25/mese",
    cta: "Scopri Pro",
    later: "Forse più tardi",
  },
  fr: {
    title: "Tes analyses photo gratuites sont épuisées pour ce mois-ci",
    body: "Bravo pour ta régularité ! Tu as utilisé tes 5 analyses photo gratuites ce mois-ci. Avec Pro, tu continues sans limites — plus menus hebdo, garde-manger, IA restaurant et ton coach.",
    lastTitle: "C'était ta dernière analyse photo gratuite 🎉",
    lastBody: "C'est fait ! Tu as utilisé tes 5 analyses photo gratuites ce mois-ci — belle régularité. Avec Pro, tu continues simplement sans limites, plus menus hebdo, garde-manger, IA restaurant et ton coach.",
    featureTitle: "C'est une fonction Pro ✨",
    featureBody: "Menus hebdo, garde-manger, IA restaurant, coach et plus font partie de Solano Essence Pro. Tu gardes quand même tes 5 analyses photo gratuites chaque mois pour ton journal.",
    reset: "Tes analyses photo gratuites se rechargent automatiquement au début de chaque mois.",
    yearlyBadge: "2 mois offerts",
    yearlyLine: "Abonnement annuel : paie 10 mois, reçois 12 — soit CHF 8.25/mois",
    cta: "Découvrir Pro",
    later: "Peut-être plus tard",
  },
};

/** Friendly bottom-sheet shown when the backend answers 402 UPGRADE_REQUIRED.
 *  Mounted once globally; http.js dispatches "solano:upgrade-required". */
export default function UpgradeLimitDialog() {
  const { lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState("limit");
  const c = COPY[lang] || COPY.de;

  useEffect(() => {
    const guard = (fn) => (e) => {
      if (!window.location.pathname.startsWith("/app/abo")) fn(e);
    };
    const onLimit = guard((e) => {
      setVariant(e?.detail?.reason === "pro_feature" ? "feature" : "limit");
      setOpen(true);
    });
    const onLast = guard(() => { setVariant("last"); setOpen(true); });
    window.addEventListener("solano:upgrade-required", onLimit);
    window.addEventListener("solano:last-free-action", onLast);
    return () => {
      window.removeEventListener("solano:upgrade-required", onLimit);
      window.removeEventListener("solano:last-free-action", onLast);
    };
  }, []);

  // Auto-close on navigation so the sheet never lingers on the next page.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="upgrade-limit-dialog">
      <div className="w-full max-w-sm rounded-3xl bg-white dark:bg-[#141819] shadow-2xl overflow-hidden">
        <div className="relative p-6 pb-4 text-center">
          <button
            onClick={() => setOpen(false)}
            data-testid="upgrade-limit-close"
            aria-label="Close"
            className="absolute top-3 right-3 p-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 active:scale-90 transition"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex justify-center"><Mascot size={96} pose="wave" alt="Solano" /></div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FC9700] to-[#d97f00] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
            <img src="https://customer-assets-agu9un31.emergentagent.net/job_nutrifit-client-1/artifacts/1xx7d8jc_IMG_0496.webp" alt="" className="h-3 w-3 object-contain brightness-0 invert" /> Solano Essence Pro
          </div>
          <h2 className="mt-3 font-heading text-lg font-bold text-[#1A1D1E] dark:text-white leading-snug" data-testid="upgrade-limit-title">
            {variant === "last" ? c.lastTitle : variant === "feature" ? c.featureTitle : c.title}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {variant === "last" ? c.lastBody : variant === "feature" ? c.featureBody : c.body}
          </p>
          {/* Yearly-plan value highlight — nudges toward the annual subscription */}
          <div className="mt-3 rounded-2xl border border-[#E8951C]/30 bg-[#FFF7EB] dark:bg-[#2a2116] dark:border-[#E8951C]/25 px-4 py-3 text-left" data-testid="upgrade-limit-yearly-hint">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#E8951C] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5">
              <Gift className="h-2.5 w-2.5" /> {c.yearlyBadge}
            </span>
            <p className="mt-1.5 text-xs font-medium text-[#8a5a10] dark:text-[#f0c079] leading-snug">{c.yearlyLine}</p>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">{c.reset}</p>
        </div>
        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={() => { setOpen(false); navigate("/app/abo"); }}
            data-testid="upgrade-limit-cta"
            className="w-full h-12 rounded-full text-white font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition"
            style={{ background: "linear-gradient(135deg, #E8951C, #C97B5B)" }}
          >
            <Sparkles className="h-4 w-4" /> {c.cta}
          </button>
          <button
            onClick={() => setOpen(false)}
            data-testid="upgrade-limit-later"
            className="w-full h-10 text-sm font-semibold text-slate-500 dark:text-slate-400 active:opacity-70 transition"
          >
            {c.later}
          </button>
        </div>
      </div>
    </div>
  );
}
